import { useState } from 'react';

const LOAN_TYPES = [
    { value: '30-year-fixed', label: 'Conventional 30-Year Fixed', years: 30 },
    { value: '15-year-fixed', label: 'Conventional 15-Year Fixed', years: 15 },
    { value: '20-year-fixed', label: 'Conventional 20-Year Fixed', years: 20 },
    { value: '25-year-fixed', label: 'Conventional 25-Year Fixed', years: 25 },
    { value: '5-1-arm', label: 'Conventional 5/1 ARM', years: 30 },
    { value: '7-1-arm', label: 'Conventional 7/1 ARM', years: 30 },
    { value: '10-1-arm', label: 'Conventional 10/1 ARM', years: 30 }
];

const BUYDOWN_OPTIONS = [
    { value: 'none', label: 'None' },
    { value: '1-0', label: '1-0 Buydown' },
    { value: '2-1', label: '2-1 Buydown' },
    { value: '3-2-1', label: '3-2-1 Buydown' }
];

function LoanInputForm({ loanData, setLoanData }) {
    const handleInputChange = (field, value) => {
        setLoanData(prev => {
            // Parse numeric values to prevent string concatenation
            let parsedValue = value;
            if (['homePrice', 'downPayment', 'interestRate', 'downPaymentPercent'].includes(field)) {
                parsedValue = value === '' ? 0 : parseFloat(value);
            }

            const updates = { [field]: parsedValue };

            // Handle down payment conversions
            if (field === 'homePrice' || field === 'downPayment') {
                const price = field === 'homePrice' ? parsedValue : prev.homePrice;
                const down = field === 'downPayment' ? parsedValue : prev.downPayment;
                updates.downPaymentPercent = price > 0 ? ((down / price) * 100).toFixed(2) : 0;
            }

            if (field === 'downPaymentPercent') {
                const percent = parsedValue;
                updates.downPayment = (prev.homePrice * (percent / 100)).toFixed(2);
            }

            // Handle loan type changes
            if (field === 'loanType') {
                const selectedType = LOAN_TYPES.find(t => t.value === value);
                if (selectedType) {
                    updates.loanYears = selectedType.years;
                }
            }

            return { ...prev, ...updates };
        });
    };

    return (
        <div className="card">
            <div className="card-header">
                <h3 className="card-title">Loan Details</h3>
            </div>

            <div className="form-group">
                <label className="form-label">Home Price</label>
                <div className="input-group">
                    <span className="input-prefix">$</span>
                    <input
                        type="number"
                        className="form-input"
                        value={loanData.homePrice}
                        onChange={(e) => handleInputChange('homePrice', e.target.value)}
                        min="0"
                        step="1000"
                    />
                </div>
            </div>

            <div className="grid grid-2">
                <div className="form-group">
                    <label className="form-label">Down Payment ($)</label>
                    <div className="input-group">
                        <span className="input-prefix">$</span>
                        <input
                            type="number"
                            className="form-input"
                            value={loanData.downPayment}
                            onChange={(e) => handleInputChange('downPayment', e.target.value)}
                            min="0"
                            step="1000"
                        />
                    </div>
                </div>

                <div className="form-group">
                    <label className="form-label">Down Payment (%)</label>
                    <div className="input-group">
                        <input
                            type="number"
                            className="form-input"
                            value={loanData.downPaymentPercent}
                            onChange={(e) => handleInputChange('downPaymentPercent', e.target.value)}
                            min="0"
                            max="100"
                            step="0.1"
                        />
                        <span className="input-suffix">%</span>
                    </div>
                </div>
            </div>

            <div className="form-group">
                <label className="form-label">Interest Rate</label>
                <div className="input-group">
                    <input
                        type="number"
                        className="form-input"
                        value={loanData.interestRate}
                        onChange={(e) => handleInputChange('interestRate', e.target.value)}
                        min="0"
                        max="20"
                        step="0.125"
                    />
                    <span className="input-suffix">%</span>
                </div>
            </div>

            <div className="form-group">
                <label className="form-label">Zip Code</label>
                <input
                    type="text"
                    className="form-input"
                    value={loanData.zipCode}
                    onChange={(e) => handleInputChange('zipCode', e.target.value)}
                    placeholder="Enter zip code"
                    maxLength="5"
                />
            </div>

            <div className="form-group">
                <label className="form-label">Loan Type</label>
                <select
                    className="form-select"
                    value={loanData.loanType}
                    onChange={(e) => handleInputChange('loanType', e.target.value)}
                >
                    {LOAN_TYPES.map(type => (
                        <option key={type.value} value={type.value}>
                            {type.label}
                        </option>
                    ))}
                </select>
            </div>

            <div className="form-group">
                <label className="form-label">Buydown Option</label>
                <select
                    className="form-select"
                    value={loanData.buydownType}
                    onChange={(e) => handleInputChange('buydownType', e.target.value)}
                >
                    {BUYDOWN_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </div>

            {loanData.buydownType !== 'none' && (
                <div className="alert alert-info">
                    <strong>Buydown Info:</strong>
                    {loanData.buydownType === '1-0' && ' 1% lower rate in year 1'}
                    {loanData.buydownType === '2-1' && ' 2% lower in year 1, 1% lower in year 2'}
                    {loanData.buydownType === '3-2-1' && ' 3%, 2%, and 1% lower for the first 3 years'}
                </div>
            )}
        </div>
    );
}

export default LoanInputForm;
